import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { Form } from '@components/ui/form';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { useAddTaskScheduleForm } from '@forms/jobs/addTaskSchedule';
import { JobStageScheduleDialogDays } from './days';
import { JobStageScheduleDialogTeam } from './team';
import { JobStageScheduleDialogProps } from './types';

export const JobStageScheduleDialog = ({
  jobId,
  stageId,
  task,
  setTask,
  onAddWithoutSchedule,
}: JobStageScheduleDialogProps) => {
  const { t } = useTranslation('app');
  const popoverRef = useRef<HTMLDivElement>(null);
  const open = Boolean(task);

  const { form, onSaveDraft, onSend, locked } = useAddTaskScheduleForm({
    jobId,
    stageId,
    task,
    successHandler: () => setTask(undefined),
  });

  const { reset, watch } = form;
  const foremanId = watch('foremanId');

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={(value) => !value && setTask(undefined)}>
      <DialogContent className="max-h-[95dvh] overflow-y-auto sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{t('pages.job.addTaskSchedule.title', { name: task?.name })}</DialogTitle>
        </DialogHeader>
        <div ref={popoverRef} className="overflow-visible" />
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void onSaveDraft();
            }}>
            <JobStageScheduleDialogDays />
            <JobStageScheduleDialogTeam popoverRef={popoverRef} />
            <div className="flex flex-col gap-2 border-t border-neutral-200 pt-4">
              <span className="text-right text-sm text-neutral-700">
                {t('schedules.saveAsDraftTooltip')}
              </span>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  variant="ghost"
                  disabled={locked}
                  onClick={() => {
                    if (!task) return;
                    onAddWithoutSchedule(task);
                    setTask(undefined);
                  }}>
                  {t('pages.job.addTaskSchedule.withoutSchedule')}
                </Button>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
                  <Button variant="tertiary" type="submit" disabled={locked}>
                    {t('actions.saveAsDraft')}
                  </Button>
                  {foremanId && (
                    <Button disabled={locked} onClick={() => void onSend()}>
                      <SvgConsumer id="message" className="text-lg" />
                      {t('actions.sendSchedule')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
